<template>
  <div class="json-table-converter-component">
    <h2>Pretvornik JSON-a v tabelo</h2>
    <p>
      Vnesi JSON, ki vsebuje array objektov. Izberi stolpce, ki jih želiš
      prikazati. Stolpca 'tokens', 'loyalty' in 'usd' se samodejno pretvorijo.
    </p>

    <div class="input-container">
      <label for="text-input">Vnesi JSON:</label>
      <textarea
        id="text-input"
        v-model="textInput"
        placeholder="Vnesite JSON array objektov..."
      ></textarea>
    </div>

    <div class="filter-container">
      <h3>Izbira stolpcev:</h3>
      <div class="checkbox-grid-columns">
        <div v-for="key in allPossibleHeaders" :key="key" class="checkbox-item">
          <input
            type="checkbox"
            :id="'col-' + key"
            :value="key"
            v-model="selectedHeaders"
          />
          <label :for="'col-' + key">{{ key }}</label>
        </div>
      </div>
      <button @click="clearSelectedHeaders" class="clear-filters-button">
        Počisti izbiro stolpcev
      </button>
    </div>

    <div class="filter-container">
      <h3>Filtriraj po tipu:</h3>
      <div class="checkbox-grid">
        <div v-for="option in typeOptions" :key="option" class="checkbox-item">
          <input
            type="checkbox"
            :id="option"
            :value="option"
            v-model="selectedTypes"
          />
          <label :for="option">{{ option }}</label>
        </div>
      </div>
      <button @click="clearFilters" class="clear-filters-button">
        Počisti filtre
      </button>
    </div>

    <div class="button-container">
      <button @click="convertToTable">Prikaži v tabeli</button>
      <button @click="showAvailableHTL" class="available-button">
        Available HTL
      </button>
    </div>

    <div
      v-if="filteredTableData.length && tableHeaders.length"
      class="output-container"
    >
      <h3>Izhodna tabela:</h3>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th v-for="key in tableHeaders" :key="key">{{ key }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in filteredTableData" :key="index">
              <td v-for="key in tableHeaders" :key="key">
                {{ item[key] !== null ? item[key] : "" }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div v-if="availableTableData.length" class="output-container">
      <h3>Available HTL tabela:</h3>
      <div class="table-container">
        <table>
          <thead>
            <tr>
              <th>createdAt</th>
              <th>available</th>
              <th>total</th>
              <th>type</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in availableTableData" :key="index">
              <td>{{ item.createdAt }}</td>
              <td>{{ item.available }}</td>
              <td>{{ item.total }}</td>
              <td>{{ item.type }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <p
      v-else-if="
        tableData.length && (!filteredTableData.length || !tableHeaders.length)
      "
      class="info-message"
    >
      Ni rezultatov za izbrane filtre ali stolpce.
    </p>
    <p v-if="error" class="error-message">{{ error }}</p>
  </div>
</template>

<script>
export default {
  name: "8d8b",
  data() {
    return {
      textInput: "",
      tableData: [],
      tableHeaders: [],
      availableTableData: [],
      error: null,
      typeOptions: [
        "availableToSharesB",
        "binaryCommission",
        "bonus",
        "budgetModify",
        "buyback",
        "conversion",
        "convertShareAToShare",
        "convertTokensToHeld",
        "convertTokensToShare",
        "convertTokensToShareA",
        "Crezervacija",
        "deposit",
        "directCommission",
        "dividends",
        "donateTokens",
        "ERABought",
        "exchangeUSDC2HTL",
        "exchangeUSDT2HTL",
        "freedomPortfolioLevelUpdate",
        "freedomProgramDeposit",
        "init",
        "internalCorrection",
        "matchingCommission",
        "moveAllToAvailable",
        "moveToAvailable",
        "newRank",
        "oneTimeMoveAvailableToUSDT",
        "packageBought",
        "packageReleasedEarly",
        "packageReward",
        "packageToSharesB",
        "packageUpgraded",
        "returnedSharesA",
        "rezervacija",
        "secureSell",
        "secureSellRequest",
        "securesellCanceled",
        "sellBackRequest",
        "sellbackCanceled",
        "sellbackConfirmed",
        "startFreedomProgram",
        "ticketBought",
        "ticketReceived",
        "ticketSend",
        "transfer",
        "withdrawal",
        "withdrawalCanceled",
        "withdrawalConfirmed",
        "withdrawalRequest",
      ].sort(),
      allPossibleHeaders: [
        "identificator",
        "createdAt",
        "type",
        "rank",
        "oldRank",
        "tokens",
        "loyalty",
        "usd",
        "inactive",
        "held",
        "shares",
        "sharesA",
        "promotionalSharesA",
        "txCurrency",
        "txValue",
        "txTo",
        "poolId",
        "poolMultiplier",
        "poolStatus",
        "oldPoolStatus",
        "comment",
        "tickets",
      ],
      selectedHeaders: [],
      selectedTypes: [],
    };
  },
  computed: {
    filteredTableData() {
      if (this.selectedTypes.length === 0) {
        return this.tableData;
      }
      return this.tableData.filter((item) =>
        this.selectedTypes.includes(item.type)
      );
    },
  },
  methods: {
    convertToTable() {
      this.tableData = [];
      this.tableHeaders = [];
      this.error = null;
      this.selectedTypes = [];
      this.availableTableData = [];

      if (!this.textInput.trim()) {
        this.error = "Vnesi JSON za pretvorbo.";
        return;
      }
      if (this.selectedHeaders.length === 0) {
        this.error = "Izberi vsaj en stolpec za prikaz.";
        return;
      }

      try {
        const parsedJson = JSON.parse(this.textInput);

        if (Array.isArray(parsedJson) && parsedJson.length > 0) {
          const processedData = parsedJson.map((item) => {
            if (item.usdt !== null && item.usdc !== null) {
              throw new Error("Napaka! V obeh poljih USD je vrednost!");
            }

            const usdValue = item.usdt !== null ? item.usdt : item.usdc;
            const finalUsd =
              usdValue !== null ? (usdValue / 100000000).toFixed(8) : null;

            const finalTokens =
              item.tokens !== null
                ? (item.tokens / 100000000).toFixed(8)
                : null;

            const finalLoyalty =
              item.loyalty !== null
                ? (item.loyalty / 100000000).toFixed(8)
                : null;

            return {
              ...item,
              tokens: finalTokens,
              usd: finalUsd,
              loyalty: finalLoyalty,
            };
          });

          this.tableHeaders = this.selectedHeaders;
          this.tableData = processedData;
        } else {
          this.error = "Vneseni JSON mora biti array objektov.";
        }
      } catch (e) {
        this.error = e.message;
        console.error(e);
      }
    },
    showAvailableHTL() {
      if (!this.tableData.length) {
        this.error = "Najprej moraš pretvoriti JSON v tabelo.";
        return;
      }

      const filteredAndMappedData = this.tableData
        .filter((item) => item.tokens !== null || item.loyalty !== null)
        .map((item) => {
          let availableValue = null;
          if (item.tokens !== null && item.loyalty !== null) {
            availableValue = (
              parseFloat(item.tokens) + parseFloat(item.loyalty)
            ).toFixed(8);
          } else if (item.tokens !== null) {
            availableValue = item.tokens;
          } else if (item.loyalty !== null) {
            availableValue = item.loyalty;
          }

          return {
            createdAt: item.createdAt,
            available: availableValue,
            type: item.type, // Dodan 'type'
          };
        });

      // Sortiraj po datumu (createdAt) od najstarejšega do najnovejšega
      const sortedData = filteredAndMappedData.sort((a, b) => {
        return new Date(a.createdAt) - new Date(b.createdAt);
      });

      // Izračunaj kumulativno vsoto (total) z uporabo forEach
      let cumulativeTotal = 0;
      sortedData.forEach((item) => {
        const availableValue = parseFloat(item.available);
        cumulativeTotal += availableValue;
        item.total = cumulativeTotal.toFixed(8);
      });

      this.availableTableData = sortedData;

      this.error = null;
    },
    clearFilters() {
      this.selectedTypes = [];
    },
    clearSelectedHeaders() {
      this.selectedHeaders = [];
    },
  },
};
</script>

<style scoped>
.json-table-converter-component {
  font-family: Arial, sans-serif;
  max-width: 1000px;
  margin: 40px auto;
  padding: 20px;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

h2 {
  color: #333;
  text-align: center;
}

p {
  color: #666;
  text-align: center;
}

.input-container,
.filter-container,
.button-container,
.output-container {
  margin-top: 20px;
}

label {
  display: block;
  margin-bottom: 8px;
  font-weight: bold;
}

textarea {
  width: 100%;
  min-height: 250px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: monospace;
  font-size: 14px;
  resize: vertical;
}

.filter-container {
  background-color: #f9f9f9;
  border: 1px solid #eee;
  padding: 15px;
  border-radius: 5px;
  margin-top: 20px;
}

.filter-container h3 {
  text-align: center;
  margin-bottom: 15px;
  color: #333;
}

.checkbox-grid,
.checkbox-grid-columns {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
  margin-bottom: 15px;
}

.checkbox-grid-columns {
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
}

.checkbox-item {
  display: flex;
  align-items: center;
}

.checkbox-item input[type="checkbox"] {
  margin-right: 8px;
  transform: scale(1.2);
}

.checkbox-item label {
  margin-bottom: 0;
  font-weight: normal;
  color: #555;
  white-space: nowrap;
}

.clear-filters-button {
  display: block;
  width: auto;
  margin: 10px auto 0 auto;
  padding: 8px 15px;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  transition: background-color 0.3s ease;
}

.clear-filters-button:hover {
  background-color: #5a6268;
}

.button-container {
  text-align: center;
  margin-top: 20px;
}

button {
  padding: 10px 20px;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 16px;
  transition: background-color 0.3s ease;
}

.available-button {
  background-color: #28a745;
  margin-left: 10px;
}

.available-button:hover {
  background-color: #218838;
}

.output-container {
  margin-top: 20px;
  text-align: left;
}

.table-container {
  overflow-x: auto;
}

table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 15px;
}

th,
td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
  white-space: nowrap;
}

th {
  background-color: #f2f2f2;
  font-weight: bold;
}

.error-message {
  color: #dc3545;
  text-align: center;
  margin-top: 10px;
}

.info-message {
  color: #17a2b8;
  text-align: center;
  margin-top: 10px;
}
</style>